import classNames from "classnames";
import { useState } from "react";
import { Col, Row } from "react-grid-system";
import {
  CMInfoUpdateSectionType,
  Option,
  ValidationErrors,
} from "../../../../lib/api/models";
import { CMINFO_OTHER_LANGUAGE_PREFERENCE_ID } from "../../../../lib/validation/schemas";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import StyledInput from "../../../common/inputs/StyledInput";
import Select from "../../../common/inputs/Select";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoLanguageSubtool.module.css";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";
import { partialSuccessErrorMessage } from "../../constants";

const strings = {
  language: "Language",
  select: "Select",
  otherLanguage: "Other Language",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
};

type Props = {
  isScriptOpen: boolean;
  onSetScriptOpen: (open: boolean) => void;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.Language];
  initialFormState: CMInfoFormInput[CMInfoSubtool.Language];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.Language]>,
  ) => void;
  languageOptions: Option[];
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  validationMessages: ValidationErrors | undefined;
  className?: string;
};

export default function CMInfoLanguageSubtool({
  isScriptOpen,
  onSetScriptOpen,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  languageOptions,
  onUpdate,
  currentlyUpdating,
  failedSections,
  validationMessages,
  className,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(
    formState.languageDueForVerification,
  );
  const [hasUnverifiedChanges, setHasUnverifiedChanges] = useState(false);

  const isOtherLanguage =
    formState.languagePreferenceId === CMINFO_OTHER_LANGUAGE_PREFERENCE_ID;

  return (
    <Collapsible
      disabled={
        formState.languageDueForVerification || formState.isLanguageVerified
      }
      headerText={strings.language}
      isExpanded={isExpanded}
      onClick={() => {
        setIsExpanded(!isExpanded);
        if (isExpanded) {
          onSetScriptOpen(false);
        }
      }}
      className={className}
    >
      <Row>
        <Col md={4.5}>
          <Select
            options={languageOptions.map((option) => ({
              value: option.id,
              label:
                option.value[0].toUpperCase() +
                option.value.slice(1).toLowerCase(),
            }))}
            selectedValue={formState.languagePreferenceId}
            onChange={(languagePreferenceId) => {
              generalDispatch({
                languagePreferenceId,
                isLanguageVerified: true,
                otherLanguage: "",
              });
              setHasUnverifiedChanges(true);
            }}
            aria-label={strings.language}
            placeholder={strings.select}
            validationMessages={validationMessages?.languagePreferenceId}
          />
        </Col>
        <Col offset={{ md: 1.3 }} md={6.2} className={styles.rightHandCol}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.languageLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isLanguageVerified}
            onCheck={(isLanguageVerified) =>
              generalDispatch({ isLanguageVerified })
            }
            isExpired={formState.languageDueForVerification}
            disabled={hasUnverifiedChanges}
          />
        </Col>
      </Row>
      <Row>
        {isOtherLanguage ? (
          <Col md={4.5}>
            <StyledInput
              className={styles.otherLanguage}
              label={strings.otherLanguage}
              value={formState.otherLanguage}
              onChange={(e) => {
                generalDispatch({
                  otherLanguage: e.currentTarget.value,
                  isLanguageVerified: true,
                });
                setHasUnverifiedChanges(true);
              }}
              validationMessages={validationMessages?.otherLanguage}
              maxLength={27}
            />
          </Col>
        ) : null}
        <Col className={classNames(styles.languageSubtoolButtons, styles.mt)}>
          <CMInfoSubtoolButtons
            isScriptOpen={isScriptOpen}
            onSetScriptOpen={onSetScriptOpen}
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                generalDispatch(initialFormState);
                setHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!formState.isLanguageVerified}
            onClear={() =>
              onShowConfirmationModal("clear", () => {
                generalDispatch({
                  languagePreferenceId: "",
                  otherLanguage: "",
                  isLanguageVerified: true,
                });
                setHasUnverifiedChanges(true);
              })
            }
            isClearDisabled={
              formState.languagePreferenceId === "" &&
              formState.otherLanguage === "" &&
              (!formState.isLanguageVerified || hasUnverifiedChanges)
            }
            onUpdate={onUpdate}
            isUpdateDisabled={
              !formState.isLanguageVerified || !!currentlyUpdating
            }
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.Language}
          />
        </Col>
      </Row>
      {failedSections.includes("language") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.language),
              ]}
            />
          </Col>
        </Row>
      ) : null}
    </Collapsible>
  );
}
